import isEmpty from 'lodash/isEmpty';
import mixpanel from 'mixpanel-browser';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ACTIONS,
  type CallBackProps,
  EVENTS,
  STATUS,
  type Step,
} from 'react-joyride';
import { useNavigate } from 'react-router';
import { useMutation } from 'urql';

import { useGetLoggedInUser } from '@revelio/auth';
import {
  InitiationTrackingEvents,
  PagePaths,
  useTourStore,
} from '@revelio/core';
import {
  type KeyValueResp,
  MetadataKey,
  type User,
} from '@revelio/data-access';

import { UpdateUserMutation } from '../adminRewrite/userOperations';
import { isElementVisible, pollUntil } from './tour.utils';

/**
 * Everything the tour needs to drive Joyride, minus the choreography.
 *
 * The between-step choreography (expanding sections of the rail) lives with
 * the tour itself; this module holds the machinery underneath: clamped step
 * index, waiting for a step's DOM target to exist and become visible, the
 * remount workaround, and writing `tour_completed` on the way out. The tour
 * injects its own `onStepForward` / `onStepBack`.
 */

/**
 * Joyride does not reliably re-render its tooltip when `stepIndex` is set to
 * the value it already holds (e.g. during back-navigation to the current step).
 * Bumping a key forces a full remount, which repositions the tooltip onto the
 * correct target. See https://github.com/gilbarbara/react-joyride/issues/641
 */
export interface TourRunner {
  stepIndexState: number;
  tourRenderKey: number;
  stepsWithOverrides: Step[];
  handleJoyrideCallback: (data: CallBackProps) => void;
}

export interface UseTourRunnerArgs {
  steps: Step[];
  /** Side effects to run when advancing past `data.step`. */
  onStepForward: (data: CallBackProps) => Promise<void> | void;
  /** Side effects to run when stepping back from `data.step`. */
  onStepBack: (data: CallBackProps) => Promise<void> | void;
  /** Reset any tour-owned UI state. Runs before navigation on finish/skip. */
  onComplete: () => void;
}

export function useTourRunner({
  steps,
  onStepForward,
  onStepBack,
  onComplete,
}: UseTourRunnerArgs): TourRunner {
  const navigate = useNavigate();
  const [, updateUser] = useMutation(UpdateUserMutation);
  const { loggedInUser, refetch: refetchUserData } = useGetLoggedInUser();

  const setStepIndex = useTourStore((s) => s.setStepIndex);
  const setIsTour = useTourStore((s) => s.setIsTour);
  const setIsTourComplete = useTourStore((s) => s.setIsTourComplete);

  const [stepIndexState, setStepIndexState] = useState(0);
  const [tourRenderKey, setTourRenderKey] = useState(0);
  const [targetOverrides, setTargetOverrides] = useState<
    Record<number, string>
  >({});
  const stepIndexRef = useRef(0);
  /**
   * Guard against spurious Joyride callbacks during programmatic step
   * transitions. When we set stepIndex ourselves (e.g. back navigation),
   * Joyride can fire extra step:after events that don't correspond to a user
   * action, looping between steps. Set when a step:after starts processing,
   * cleared when Joyride renders the tooltip for the new step.
   */
  const isProcessingStepChangeRef = useRef(false);
  const cancelPollingRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      cancelPollingRef.current?.();
      cancelPollingRef.current = null;
    };
  }, []);

  const updateStepIndexState = useCallback(
    (nextStepIndex: number, options: { forceRender?: boolean } = {}) => {
      const { forceRender = false } = options;
      const maxIndex = Math.max(steps.length - 1, 0);
      const clampedIndex = Math.min(Math.max(nextStepIndex, 0), maxIndex);
      const previousIndex = stepIndexRef.current;

      stepIndexRef.current = clampedIndex;
      setStepIndex(clampedIndex);
      setStepIndexState(clampedIndex);

      if (forceRender && previousIndex === clampedIndex) {
        setTourRenderKey((key) => key + 1);
      }

      // Clear the processing guard — the tooltip event doesn't always fire when
      // stepIndex changes without a full remount, leaving the guard stuck.
      isProcessingStepChangeRef.current = false;
    },
    [setStepIndex, steps.length]
  );

  // Steps are built from the user's permissions and can shrink between
  // renders; clamp rather than leaving Joyride pointing past the end.
  useEffect(() => {
    const maxIndex = Math.max(steps.length - 1, 0);
    if (stepIndexRef.current > maxIndex) {
      updateStepIndexState(maxIndex, { forceRender: true });
    }
  }, [steps.length, updateStepIndexState]);

  // ─── Target override helpers ───────────────────────────────────────────────
  // When a step's DOM target isn't found we temporarily override it to 'body'
  // so Joyride renders a centered tooltip instead of erroring out.

  const applyTargetOverride = useCallback(
    (stepIndex: number) => {
      setTargetOverrides((previous) =>
        previous[stepIndex] === 'body'
          ? previous
          : { ...previous, [stepIndex]: 'body' }
      );
      updateStepIndexState(stepIndex, { forceRender: true });
    },
    [updateStepIndexState]
  );

  const clearTargetOverride = useCallback((stepIndex: number) => {
    setTargetOverrides((previous) => {
      if (!previous[stepIndex]) {
        return previous;
      }

      const nextOverrides = { ...previous };
      delete nextOverrides[stepIndex];
      return nextOverrides;
    });
  }, []);

  const updateStepIndexWhenTargetReady = useCallback(
    (
      desiredStepIndex: number,
      fallbackStepIndex?: number,
      options: { allowImmediateFallback?: boolean } = {}
    ) => {
      const hasValidFallback =
        fallbackStepIndex !== undefined &&
        fallbackStepIndex >= 0 &&
        fallbackStepIndex < steps.length;

      if (desiredStepIndex < 0 || desiredStepIndex >= steps.length) {
        if (hasValidFallback && fallbackStepIndex !== undefined) {
          updateStepIndexState(fallbackStepIndex);
        }
        return;
      }

      const target = steps[desiredStepIndex]?.target;
      const { allowImmediateFallback = false } = options;

      const resolveWhenVisible = (predicate: () => boolean) => {
        cancelPollingRef.current?.();
        cancelPollingRef.current = pollUntil(
          predicate,
          () => {
            clearTargetOverride(desiredStepIndex);
            updateStepIndexState(desiredStepIndex, { forceRender: true });
            cancelPollingRef.current = null;
          },
          () => {
            applyTargetOverride(desiredStepIndex);
            cancelPollingRef.current = null;
          }
        );
      };

      // Body targets are always available.
      if (target === 'body') {
        updateStepIndexState(desiredStepIndex, {
          forceRender: desiredStepIndex === stepIndexRef.current,
        });
        return;
      }

      // Direct HTMLElement reference.
      if (target instanceof HTMLElement) {
        if (isElementVisible(target)) {
          clearTargetOverride(desiredStepIndex);
          updateStepIndexState(desiredStepIndex, { forceRender: true });
          return;
        }
        // Element exists but is hidden — poll for it to become visible.
        resolveWhenVisible(() => isElementVisible(target));
        return;
      }

      // CSS selector string.
      if (typeof target === 'string') {
        if (allowImmediateFallback) {
          const immediateTarget = document.querySelector(target);
          if (
            !(immediateTarget instanceof HTMLElement) ||
            !isElementVisible(immediateTarget)
          ) {
            applyTargetOverride(desiredStepIndex);
          }
        }

        resolveWhenVisible(() => {
          const element = document.querySelector(target);
          return element instanceof HTMLElement && isElementVisible(element);
        });
        return;
      }

      // Unknown target type — just move to the step.
      updateStepIndexState(desiredStepIndex, {
        forceRender: desiredStepIndex === stepIndexRef.current,
      });
    },
    [applyTargetOverride, clearTargetOverride, steps, updateStepIndexState]
  );

  const handleUpdateMetadata = useCallback(
    (user: User = {}, newMeta: KeyValueResp[]) => {
      const metaFieldsToExclude = [MetadataKey.ExpirationDate];

      return new Promise((resolve, reject) => {
        if (isEmpty(user)) {
          reject('No logged in user provided');
        }

        const newMetaKeys = newMeta.map((item) => item.key);

        const currentMetaObjects = (user.metadata || []).filter(
          (item: KeyValueResp | null) =>
            !newMetaKeys.includes(item?.key) &&
            !metaFieldsToExclude.includes(item?.key as MetadataKey)
        );

        updateUser({
          id: user?.id as string,
          metadata: [...currentMetaObjects, ...newMeta],
        })
          .then(() => {
            refetchUserData();
            resolve('User updated');
          })
          .catch(() => {
            reject('Something went wrong');
          });
      });
    },
    [refetchUserData, updateUser]
  );

  const completeTour = useCallback(
    (skipped: boolean, index: number) => {
      onComplete();
      setIsTour(false);
      updateStepIndexState(0);
      setIsTourComplete(true);

      handleUpdateMetadata(loggedInUser, [
        { key: MetadataKey.TourCompleted, value: 'true' },
      ]);

      mixpanel.track(
        skipped
          ? InitiationTrackingEvents.SKIP_TOUR
          : InitiationTrackingEvents.FINISH_TOUR,
        skipped ? { step_index: index } : {}
      );

      navigate(`${PagePaths.LANDING}`);
    },
    [
      handleUpdateMetadata,
      loggedInUser,
      navigate,
      onComplete,
      setIsTour,
      setIsTourComplete,
      updateStepIndexState,
    ]
  );

  /**
   * Joyride's callback prop is synchronous but our step transitions do async
   * work (navigation, waiting for a panel to mount). The async body runs in a
   * void IIFE so rejections surface here rather than being swallowed by a
   * Promise Joyride ignores.
   */
  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, type, action, index } = data;

      if (type === EVENTS.TOUR_END && status === STATUS.SKIPPED) {
        completeTour(true, index);
        return;
      }

      // Joyride fires a 'tooltip' event (absent from its public typings) once
      // the tooltip has rendered — the signal that a transition is complete.
      if (type === ('tooltip' as unknown as typeof type)) {
        isProcessingStepChangeRef.current = false;
      }

      const nextStepEvents = [
        EVENTS.STEP_AFTER,
        EVENTS.TARGET_NOT_FOUND,
      ] as string[];
      if (!nextStepEvents.includes(type)) {
        return;
      }

      if (type === EVENTS.STEP_AFTER && isProcessingStepChangeRef.current) {
        return;
      }
      isProcessingStepChangeRef.current = true;

      const isStepBack = action === ACTIONS.PREV;
      const nextStepIndex = index + (isStepBack ? -1 : 1);

      if (
        type === EVENTS.STEP_AFTER &&
        action === ACTIONS.NEXT &&
        index === steps.length - 1
      ) {
        completeTour(false, index);
        return;
      }

      void (async () => {
        try {
          await (isStepBack ? onStepBack(data) : onStepForward(data));

          if (type === EVENTS.TARGET_NOT_FOUND) {
            updateStepIndexWhenTargetReady(index, nextStepIndex, {
              allowImmediateFallback: true,
            });
            return;
          }

          updateStepIndexWhenTargetReady(nextStepIndex);
        } catch (error) {
          console.error('Tour step transition failed:', error);
        }
      })();
    },
    [
      completeTour,
      onStepBack,
      onStepForward,
      steps.length,
      updateStepIndexWhenTargetReady,
    ]
  );

  const stepsWithOverrides = useMemo(
    () =>
      steps.map((step, index) => {
        const overrideTarget = targetOverrides[index];
        return overrideTarget ? { ...step, target: overrideTarget } : step;
      }),
    [steps, targetOverrides]
  );

  return {
    stepIndexState,
    tourRenderKey,
    stepsWithOverrides,
    handleJoyrideCallback,
  };
}
